import type { QuestionFillData } from '@repo/types/questions/QuestionFill';
import type { QuestionMatchingData } from '@repo/types/questions/QuestionMatching';
import type { QuestionMultipleData } from '@repo/types/questions/QuestionMultiple';
import type { QuestionSingleData } from '@repo/types/questions/QuestionSingle';
import type { QuestionTranslateData } from '@repo/types/questions/QuestionTranslate';

import { cn } from '@repo/lib/cn';
import { QuestionType } from '@repo/types/enums/questionType';
import { CopyCheckIcon, CopyIcon, Edit3Icon, GripVerticalIcon, TrashIcon, ChevronsDownIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Link } from 'react-router';

import { QuestionMarkedQuestionContent } from '@/components/features/quiz/question/question-marked';
import { QuestionMarkedFillContent } from '@/components/features/quiz/question/question-marked/question-fill';
import { QuestionMarkedMatchingContent } from '@/components/features/quiz/question/question-marked/question-matching';
import { QuestionMarkedMultipleContent } from '@/components/features/quiz/question/question-marked/question-multiple';
import { QuestionMarkedSingleContent } from '@/components/features/quiz/question/question-marked/question-single';
import { QuestionMarkedTranslateContent } from '@/components/features/quiz/question/question-marked/question-translate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SortableItemHandler } from '@/components/ui/dnd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuizBuilder } from '@/context/quiz-builder';
import { useElementData } from '@/hooks/element-data';
import { questionsLabel } from '@/lib/questions-labels';

interface QuestionBuilderCardProps {
  sectionIndex: number;
  questionIndex: number;
  questionType: string;
  className?: string;
  removeQuestion?: () => void;
  duplicateQuestion?: () => void;
}

export default function QuestionBuilderCard(questionBuilderCardProps: QuestionBuilderCardProps) {
  const { sectionIndex, questionIndex, questionType, className, removeQuestion, duplicateQuestion, ...props } = questionBuilderCardProps;
  const { sectionsForm } = useQuizBuilder();
  const [copied, setCopied] = useState<boolean>(false);
  const [openEditTooltip, setOpenEditTooltip] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentSize] = useElementData(contentRef);

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    removeQuestion?.();
  };
  const handleDuplicate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setCopied(true);
    setTimeout(() => setCopied(false), 850);
    duplicateQuestion?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 850);
  };

  const handleBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenEditTooltip(true);
  };

  const question = useWatch({
    control: sectionsForm.control,
    name: `content.${sectionIndex}.questions.${questionIndex}`,
  });

  const label: string = questionsLabel[questionType].label;
  const IconComponent = questionsLabel[questionType].icon;
  const hasError: boolean = !!sectionsForm.formState.errors.content?.[sectionIndex]?.questions?.[questionIndex];

  // Use scrollHeight to measure actual content height (including overflow)
  const contentHeight: number = contentRef.current?.scrollHeight ?? contentSize.height;
  const shouldShowOverflow: boolean = contentHeight > 140;
  const isCollapsed: boolean = !isExpanded && shouldShowOverflow;

  const renderQuestionContent = () => {
    if (!question) return null;

    switch (questionType) {
      case QuestionType.SINGLE:
        return <QuestionMarkedSingleContent question={question as QuestionSingleData} />;
      case QuestionType.MULTIPLE:
        return <QuestionMarkedMultipleContent question={question as QuestionMultipleData} />;
      case QuestionType.FILL:
        return <QuestionMarkedFillContent question={question as QuestionFillData} />;
      case QuestionType.MATCHING:
        return <QuestionMarkedMatchingContent question={question as QuestionMatchingData} />;
      case QuestionType.TRANSLATE:
        return <QuestionMarkedTranslateContent question={question as QuestionTranslateData} />;
      default:
        return null;
    }
  };

  return (
    <div
      data-section-index={sectionIndex}
      data-question-index={questionIndex}
      data-error={hasError}
      onClick={handleBoxClick}
      className={cn(
        'border-border-primary bg-card hover:bg-card/85 relative flex cursor-pointer gap-3 overflow-hidden rounded-lg border p-2 transition-colors select-none data-[error=true]:border-border-destructive',
        className
      )}
      {...props}
    >
      <SortableItemHandler>
        <Button type="button" variant={'ghost'} size={'sm'} className={'h-8 min-h-8 w-6 min-w-6'}>
          <GripVerticalIcon />
        </Button>
      </SortableItemHandler>
      <div className="w-full space-y-3">
        <div className="flex items-center gap-1">
          <Badge variant={'muted'} size={'sm'} className="min-w-6 shrink-0 justify-center p-0.5 text-xs">
            {String(questionIndex + 1).padStart(2, '0')}
          </Badge>
          <Badge variant={'muted'} size={'sm'}>
            <IconComponent />
            {label}
          </Badge>
          <div className="ml-auto flex gap-0.5">
            {removeQuestion && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant={'ghost'} size={'sm'} className="text-muted-foreground size-8 min-h-8 min-w-8" onClick={handleRemove}>
                    <TrashIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Usuń pytanie</TooltipContent>
              </Tooltip>
            )}
            {duplicateQuestion && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant={'ghost'} size={'sm'} className="size-8 min-h-8 min-w-8" onClick={handleDuplicate} disabled={copied}>
                    <div className={cn('transition-all', copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0')}>
                      <CopyCheckIcon className="text-success" aria-hidden="true" />
                    </div>
                    <div className={cn('absolute transition-all', copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100')}>
                      <CopyIcon aria-hidden="true" className="text-muted-foreground" />
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplikuj pytanie</TooltipContent>
              </Tooltip>
            )}

            <Tooltip open={openEditTooltip} onOpenChange={setOpenEditTooltip}>
              <TooltipTrigger asChild>
                <Button size={'sm'} variant={'ghost'} className="size-8 min-h-8 min-w-8" asChild>
                  <Link to={`section/${sectionIndex}/question/${questionIndex}`}>
                    <Edit3Icon />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Kliknij tutaj, aby edytować pytanie</TooltipContent>
            </Tooltip>
          </div>
        </div>
        {question?.content && (
          <div className="relative">
            <div ref={contentRef} className={cn('relative overflow-hidden transition-all duration-300', isCollapsed ? 'max-h-[140px]' : 'max-h-none')}>
              {question?.content && (
                <QuestionMarkedQuestionContent showQuestion={true} content={question?.content} className="p-0">
                  {renderQuestionContent()}
                </QuestionMarkedQuestionContent>
              )}
              {isCollapsed && shouldShowOverflow && (
                <div className="group from-card hover:from-card/85 pointer-events-none absolute right-0 bottom-0 left-0 h-20 bg-linear-to-t to-transparent transition-colors">
                  <div className="pointer-events-auto absolute inset-0 flex items-end justify-center pb-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                      }}
                      className="text-text-secondary min-w-28"
                    >
                      <ChevronsDownIcon />
                      Rozwiń
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {isExpanded && shouldShowOverflow && (
              <div className="mt-2 flex justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  className="min-w-28"
                >
                  <ChevronsDownIcon className="rotate-180" />
                  Zwiń
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
